import { BarChart3, ChevronDown, MoreHorizontal, Rows3 } from "lucide-react";
import { lazy, Suspense, type ReactNode } from "react";
import type { QueueEntry, QueueSnapshot } from "../../shared/global-queue";
import { handleLocalNavigation } from "../lib/navigation";
import { pullView, rankedForViewer } from "../lib/pull-view";
import { useViewerLogin } from "../hooks/useViewerLogin";
import { BrandMark } from "./BrandMark";
import { AppNavigationControls } from "./AppNavigationControls";
import { PullCard } from "./PullPresentation";
import { ThemeToggle } from "./ThemeToggle";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "./ui/popover";

const NotificationCenter = lazy(() => import("./NotificationCenter").then((module) => ({
  default: module.NotificationCenter,
})));

export type AppSurface = "home" | "queue" | "operations" | "authoring";

export function AppContextPath({ items }: { items: Array<{ label: string; href?: string }> }) {
  return (
    <nav aria-label="Page hierarchy" className="app-context-path">
      {items.map((item, index) => <span key={`${item.label}-${index}`}>{index > 0 && <i aria-hidden="true">/</i>}{item.href ? <a href={item.href} onClick={(event) => handleLocalNavigation(event, item.href!)}>{item.label}</a> : <strong>{item.label}</strong>}</span>)}
    </nav>
  );
}

function QueueDisclosure({ snapshot, nextReview }: { snapshot: QueueSnapshot; nextReview?: QueueEntry }) {
  const viewerLogin = useViewerLogin();
  const next = nextReview ?? rankedForViewer(snapshot.entries)[0];
  const entries = [...snapshot.entries].sort((left, right) => left.position - right.position);
  const activeCount = entries.filter((entry) => !["satisfied", "draft"].includes(entry.status)).length;
  return (
    <Popover>
      <PopoverTrigger render={<button aria-label="Inspect ordered queue" className="app-queue-disclosure-trigger" type="button" />}>
        <ChevronDown aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="app-queue-disclosure" sideOffset={7}>
        <PopoverHeader className="app-queue-disclosure-header">
          <PopoverTitle>Global queue status</PopoverTitle>
          <PopoverDescription>What each PR is waiting on, and what that means for you.</PopoverDescription>
        </PopoverHeader>
        <ol className="app-queue-disclosure-list">
          {entries.map((entry) => {
            const view = pullView(entry, viewerLogin);
            const isNext = next?.repository === entry.repository && next.pullNumber === entry.pullNumber;
            return (
              <li className={isNext ? "is-next" : undefined} key={view.key}>
                {isNext && <span className="app-queue-next-mark">next</span>}
                <PullCard rank={entry.position} view={view} />
              </li>
            );
          })}
        </ol>
        <footer>
          <span>{activeCount} active · {snapshot.entries.length} open {snapshot.entries.length === 1 ? "PR" : "PRs"}</span>
          <a href="/operations/queue" onClick={(event) => handleLocalNavigation(event, "/operations/queue")}>Open diagnostics</a>
        </footer>
      </PopoverContent>
    </Popover>
  );
}

export function AppShell({
  active,
  queueSnapshot,
  queueNext,
  center,
  children,
}: {
  active: AppSurface;
  queueSnapshot?: QueueSnapshot;
  queueNext?: QueueEntry;
  center?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="app-frame">
      <header className="app-header">
        <div className="app-header-start">
          <a className="brand" href="/" onClick={(event) => handleLocalNavigation(event, "/")}>
            <BrandMark /><strong>CHEW</strong>
          </a>
          <AppNavigationControls />
          <nav aria-label="Primary navigation" className="app-primary-nav">
            <a aria-current={active === "home" ? "page" : undefined} href="/" onClick={(event) => handleLocalNavigation(event, "/")}>Home</a>
            <span className="app-queue-nav">
              <a aria-current={active === "queue" ? "page" : undefined} href="/queue" onClick={(event) => handleLocalNavigation(event, "/queue")}>Queue</a>
              {queueSnapshot && <QueueDisclosure nextReview={queueNext} snapshot={queueSnapshot} />}
            </span>
          </nav>
        </div>
        <div className="app-header-center">{center ?? <AppContextPath items={[{ label: active === "home" ? "My work" : active === "queue" ? "Review queue" : active === "authoring" ? "Guide editor" : "Operations" }]} />}</div>
        <div className="app-header-tools">
          <Suspense fallback={<span aria-hidden="true" style={{ display: "block", height: 28, width: 28 }} />}>
            <NotificationCenter />
          </Suspense>
          <ThemeToggle />
          <Popover>
            <PopoverTrigger render={<button aria-label="Open operations" className={`app-operations-trigger${active === "operations" ? " is-active" : ""}`} type="button" />}>
              <MoreHorizontal aria-hidden="true" />
            </PopoverTrigger>
            <PopoverContent align="end" className="app-operations-menu" sideOffset={7}>
              <PopoverHeader>
                <PopoverTitle>Operations</PopoverTitle>
                <PopoverDescription>Team-level diagnostics and reporting.</PopoverDescription>
              </PopoverHeader>
              <a href="/operations/queue" onClick={(event) => handleLocalNavigation(event, "/operations/queue")}>
                <Rows3 aria-hidden="true" /><span><strong>Canonical queue</strong><small>Routing and sync diagnostics</small></span>
              </a>
              <a href="/operations/analytics" onClick={(event) => handleLocalNavigation(event, "/operations/analytics")}>
                <BarChart3 aria-hidden="true" /><span><strong>Team analytics</strong><small>Delivery and review flow</small></span>
              </a>
            </PopoverContent>
          </Popover>
        </div>
      </header>
      {children}
    </div>
  );
}
